
import { useState, useEffect } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { getSupportedProviders, getModelsForProvider } from "@/services/admin/providerService";
import ApiKeyManager from "./ApiKeyManager";
import { ModelProvider, AIModel, AIConfig } from "@/types/admin";
import { Loader2 } from "lucide-react";
import ModelSelector from "./ModelSelector";
import { fetchAIConfigByTask } from "@/services/admin/aiConfigService";

const AIModelConfig = () => {
  const [loading, setLoading] = useState(true);
  const [providers, setProviders] = useState<ModelProvider[]>([]);
  const [activeProvider, setActiveProvider] = useState<string | null>(null);
  const [providerModels, setProviderModels] = useState<Record<string, AIModel[]>>({});
  const [connectionStatus, setConnectionStatus] = useState<Record<string, boolean>>({});
  const [allModels, setAllModels] = useState<AIModel[]>([]);
  const [preCallConfig, setPreCallConfig] = useState<AIConfig | null>(null);
  const [postCallConfig, setPostCallConfig] = useState<AIConfig | null>(null);
  const [configLoaded, setConfigLoaded] = useState(false);

  const fetchProviders = async () => {
    try {
      setLoading(true);
      const availableProviders = await getSupportedProviders();
      setProviders(availableProviders);
      
      if (availableProviders.length > 0) {
        const firstProvider = availableProviders[0].id;
        setActiveProvider(firstProvider);
        
        // Set connection status for each provider
        const statusMap: Record<string, boolean> = {};
        availableProviders.forEach(p => {
          statusMap[p.id] = p.isConnected;
        });
        setConnectionStatus(statusMap);
        
        // Fetch models for each provider
        const modelsMap: Record<string, AIModel[]> = {};
        let combinedModels: AIModel[] = [];
        
        for (const provider of availableProviders) {
          const { models } = await getModelsForProvider(provider.id);
          modelsMap[provider.id] = models;
          combinedModels = [...combinedModels, ...models];
        }
        
        setProviderModels(modelsMap);
        setAllModels(combinedModels);
      }
      
      // Fetch existing configurations
      await fetchConfigurations();
    } catch (error) {
      console.error("Error fetching providers:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchConfigurations = async () => {
    try {
      const preCall = await fetchAIConfigByTask('pre-call');
      const postCall = await fetchAIConfigByTask('post-call');
      
      setPreCallConfig(preCall);
      setPostCallConfig(postCall);
      setConfigLoaded(true);
    } catch (error) {
      console.error("Error fetching configurations:", error);
    }
  };

  useEffect(() => {
    fetchProviders();
  }, []);

  const handleApiKeyUpdated = async () => {
    await fetchProviders();
  };

  const handleConfigSaved = async (config: AIConfig) => {
    if (config.for_task === 'pre-call') {
      setPreCallConfig(config);
    } else {
      setPostCallConfig(config);
    }
  };

  const hasConnectedProvider = Object.values(connectionStatus).some(status => status);

  return (
    <div className="space-y-6">
      {loading ? (
        <div className="flex justify-center items-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {providers.map((provider) => (
              <Card key={provider.id}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <div>
                    <CardTitle className="text-xl">{provider.name}</CardTitle>
                    <CardDescription>AI Language Model Provider</CardDescription>
                  </div>
                  <ApiKeyManager
                    provider={provider.id}
                    providerName={provider.name}
                    isConnected={connectionStatus[provider.id] || false}
                    onKeyUpdated={handleApiKeyUpdated}
                  />
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <h4 className="font-medium">Available Models:</h4>
                    {providerModels[provider.id]?.length > 0 ? (
                      <ul className="list-disc pl-5 text-sm">
                        {providerModels[provider.id].map(model => (
                          <li key={model.id}>{model.name}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        {connectionStatus[provider.id] 
                          ? "Loading models..." 
                          : "Add an API key to view available models"}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {activeProvider && (
            <Tabs defaultValue="pre-call" className="space-y-4">
              <TabsList>
                <TabsTrigger value="pre-call">Pre-Call Analysis</TabsTrigger>
                <TabsTrigger value="post-call">Post-Call Analysis</TabsTrigger>
              </TabsList>
              <TabsContent value="pre-call">
                <Card>
                  <CardHeader>
                    <CardTitle>Pre-Call Analysis Configuration</CardTitle>
                    <CardDescription>
                      Configure the AI model used to analyze lead information before calls
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {hasConnectedProvider ? (
                      <ModelSelector
                        models={allModels}
                        task="pre-call"
                        initialConfig={preCallConfig}
                        onConfigSaved={handleConfigSaved}
                      />
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        Please connect your API key first to configure models
                      </p>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
              <TabsContent value="post-call">
                <Card>
                  <CardHeader>
                    <CardTitle>Post-Call Analysis Configuration</CardTitle>
                    <CardDescription>
                      Configure the AI model used to analyze call transcripts
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {hasConnectedProvider ? (
                      <ModelSelector
                        models={allModels}
                        task="post-call"
                        initialConfig={postCallConfig}
                        onConfigSaved={handleConfigSaved}
                      />
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        Please connect your API key first to configure models
                      </p>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          )}
        </>
      )}
    </div>
  );
};

export default AIModelConfig;
